import React from 'react'
import { propType } from 'graphql-anywhere'
import {
  always,
  compose,
  cond,
  T,
  test
} from 'ramda'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import styles from './index.css'
import Screen from '../screen'
import CategoryNavigation from './category-navigation'
import CATEGORY_COMPONENT_FRAGMENT from '../../../graphql/fragments/category-component.gql'
import { getLocationBaseWithHref } from '../../lib/location'

export const getNeonCategoryMeta = (pathname, selectedCategory) => {
  return compose(cond([
    [test(/^\/tv-series\/.*/), always({
      title: `Watch ${selectedCategory.name} TV shows online with NEON`,
      description: `NEON has a great selection of ${selectedCategory.name} TV shows.`
    })],
    [test(/^\/browse-movies\/.*/), always({
      title: `Watch ${selectedCategory.name} movies online with NEON`,
      description: `NEON has a great selection of ${selectedCategory.name} movies.`
    })],
    [test(/^\/browse-rentals\/.*/), always({
      title: `Rent ${selectedCategory.name} movies online with NEON`,
      description: `Rent ${selectedCategory.name} movies online. No subscription required.`
    })],
    [test(/^\/browse-kids\/.*/), always({
      title: `Watch ${selectedCategory.name} kids shows online with NEON`,
      description: `NEON has a great selection of ${selectedCategory.name} kids shows.`
    })],
    [test(/^\/browse-max\/.*/), always({
      title: `Watch and stream ${selectedCategory.name} with NEON`,
      description: `NEON offers a diverse and exciting selection of the latest ${selectedCategory.name} movies and shows. Whether you're in the mood for high quality dramas, side-splitting comedies, or captivating documentaries, ${selectedCategory.name} on NEON has something for everyone. Stream your favourites anytime, anywhere, and immerse yourself in the ultimate entertainment experience.`
    })],
    [T, always({})]
  ]))(pathname)
}

/**
 * Renders both CategoryNavigation and Screen.
 * The CategoryNavigation displays category tabs or a dropdown (based on screen size),
 * while Screen loads and renders the grid tiles for the selected category.
 */
function Category({ category }) {
  const location = useLocation()
  const searchIsClicked = useSelector(state => state.navigation.searchIsClicked)
  if (!category.items.length) {
    return null
  }

  const index = category.items.findIndex(item => item.path === location.pathname)
  const selectedCategoryIdx = index === -1 ? 0 : index
  const selectedCategory = category.items[selectedCategoryIdx]
  const { title, description } = getNeonCategoryMeta(location.pathname, selectedCategory)

  return (
    <div className={styles.category}>
      <Helmet>
        <meta property="og:url" content={getLocationBaseWithHref()} />
        <meta
          name="description"
          property="og:description"
          content={description}
        />
        <meta name="title" property="og:title" content={title} />
        <title>{title}</title>
      </Helmet>
      <div className={styles.gradientTop} />
      {/* Render a sticky container when the search icon is clicked,
      providing space for the search box on small screens */}
      { searchIsClicked && <div className={styles.stickySearchBoxBar} /> }
      <CategoryNavigation
        selectedCategory={selectedCategory}
        categoryItems={category.items}
      />
      <Screen screenId={selectedCategory.id} />
    </div>
  )
}

Category.propTypes = {
  category: propType(CATEGORY_COMPONENT_FRAGMENT).isRequired
}

export default Category
